import { z } from 'zod';
import { isValidEmail } from '../email.js';
import { DomainError } from '../errors.js';
import { daysBetween } from '../local-date.js';
import { toIssues } from '../materials/catalog.js';
import type { ParseResult } from '../voyage/schemas.js';

/**
 * Felixmate müşterisi şirketin profili ve abonelik (kontrat) dönemleri (ADR 0140; ortak isteği
 * 2026-10-29, K2: "adresi vergi numarası telefonu genel irtibat emaili … kontrat başlangıç bitiş
 * süresi. yenileme yapıldığında sürenin uzatılması"). Veri control_db'dedir; panel yazar, ofis
 * yöneticisi yalnız görür. Şemalar panel sunucusu ve arayüzünde ortaktır; hata iletileri koddur.
 */

/** ISO 3166-1 alpha-2 ülke kodları (249; ISO 3166 Maintenance Agency resmî listesi). */
export const ISO_COUNTRY_CODES: readonly string[] = (
  'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ ' +
  'BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ ' +
  'CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ ' +
  'DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR ' +
  'GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ' +
  'ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ ' +
  'LA LB LC LI LK LR LS LT LU LV LY ' +
  'MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ ' +
  'NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA ' +
  'RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ ' +
  'TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ ' +
  'VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW'
).split(' ');
const COUNTRY_SET = new Set(ISO_COUNTRY_CODES);

/**
 * Telefon: isteğe bağlı `+`, rakam ve ayraç (boşluk, tire, nokta, parantez); 7–15 hane. Uluslararası
 * numara en çok 15 hanedir [ITU-T E.164 §6]; 7'den kısası santral dahilisidir.
 */
export function isValidPhone(value: string): boolean {
  const text = value.trim();
  if (!/^\+?[0-9 ().-]+$/.test(text)) return false;
  const digits = text.replace(/\D/g, '').length;
  return digits >= 7 && digits <= 15;
}

/**
 * Web adresi: şemasız yazılana `https://` eklenir; yalnız http(s) ve noktalı alan adı. Geçersizse
 * `null`.
 */
export function normalizeWebsite(value: string): string | null {
  const text = value.trim();
  if (text === '') return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(text) ? text : `https://${text}`;
  return WEBSITE.test(withScheme) ? withScheme : null;
}

/** http(s), noktalı alan adı (etiketler harf/rakam/tire), isteğe bağlı port ve yol. */
const WEBSITE =
  /^https?:\/\/(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}(?::\d{1,5})?(?:[/?#]\S*)?$/i;

const missing = (input: unknown) => input === undefined || input === null;
const typed = (code: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : code),
});
const collapse = (value: string) => value.normalize('NFC').trim().replace(/\s+/g, ' ');

/** Boş bırakılabilen metin: boşluklar sadeleşir, boş metin `null` olur. */
const optionalText = (max: number) =>
  z
    .string(typed('INVALID_TEXT'))
    .transform(collapse)
    .pipe(z.string().max(max, 'TOO_LONG'))
    .transform((value) => (value === '' ? null : value))
    .nullish()
    .transform((value) => value ?? null);

/** Boş bırakılabilen, normalize edilip doğrulanan değer. */
const optionalChecked = (
  normalize: (value: string) => string | null,
  valid: (value: string) => boolean,
  code: string,
  max: number,
) =>
  z
    .string(typed(code))
    .nullish()
    .transform((value, ctx) => {
      if (value === null || value === undefined || value.trim() === '') return null;
      const normalized = normalize(value);
      if (normalized === null || normalized.length > max || !valid(normalized)) {
        ctx.issues.push({ code: 'custom', message: code, input: value });
        return z.NEVER;
      }
      return normalized;
    });

const optionalEmail = optionalChecked(
  (value) => value.trim().toLowerCase(),
  isValidEmail,
  'INVALID_EMAIL',
  320,
);
const optionalPhone = optionalChecked(collapse, isValidPhone, 'INVALID_PHONE', 40);

/** Notun en çok uzunluğu. */
export const COMPANY_NOTES_MAX = 2000;

/**
 * Şirket profili (ADR 0140 §1). Bütün alanlar isteğe bağlıdır: şirket açılırken bilinenler girilir,
 * gerisi şirket sayfasında tamamlanır. Türkiye'de vergi numarası verildiyse 10 hane (VKN) ya da 11
 * hane (TCKN; şahıs işletmesi) ve vergi dairesi zorunlu [VUK 8; e-fatura alanları]; tedarikçi
 * profiliyle aynı kural (ADR 0114 §2).
 */
export const companyProfileSchema = z
  .strictObject({
    /** Ticari unvan / resmî ad; panelde görünen kısa ad `tenants.name`'dir. */
    legalName: optionalText(200),
    addressLine1: optionalText(200),
    addressLine2: optionalText(200),
    city: optionalText(100),
    postalCode: optionalChecked(
      (value) => collapse(value).toUpperCase(),
      (value) => /^[A-Z0-9][A-Z0-9 -]{1,11}$/.test(value),
      'INVALID_POSTAL_CODE',
      12,
    ),
    country: optionalChecked(
      (value) => value.trim().toUpperCase(),
      (value) => COUNTRY_SET.has(value),
      'INVALID_COUNTRY',
      2,
    ),
    taxOffice: optionalText(120),
    taxNumber: optionalChecked(
      collapse,
      (value) => /^[A-Za-z0-9][A-Za-z0-9 .\-/]{1,39}$/.test(value),
      'INVALID_TAX_NUMBER',
      40,
    ),
    phone: optionalPhone,
    /** Genel irtibat e-postası (ör. info@). */
    email: optionalEmail,
    website: optionalChecked(normalizeWebsite, () => true, 'INVALID_WEBSITE', 200),
    contactName: optionalText(120),
    contactTitle: optionalText(80),
    contactPhone: optionalPhone,
    contactEmail: optionalEmail,
    notes: optionalText(COMPANY_NOTES_MAX),
  })
  .superRefine((value, ctx) => {
    if (value.country !== 'TR' || value.taxNumber === null) return;
    if (!/^\d{10,11}$/.test(value.taxNumber)) {
      ctx.addIssue({ code: 'custom', message: 'INVALID_TAX_NUMBER', path: ['taxNumber'] });
    }
    if (value.taxOffice === null) {
      ctx.addIssue({ code: 'custom', message: 'TAX_OFFICE_REQUIRED', path: ['taxOffice'] });
    }
  });
export type CompanyProfile = z.output<typeof companyProfileSchema>;
export type CompanyProfileInput = z.input<typeof companyProfileSchema>;

export function parseCompanyProfile(input: unknown): ParseResult<CompanyProfile> {
  const result = companyProfileSchema.safeParse(input ?? {});
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/** Boş profil (bütün alanlar `null`). */
export const EMPTY_COMPANY_PROFILE: CompanyProfile = Object.freeze({
  legalName: null,
  addressLine1: null,
  addressLine2: null,
  city: null,
  postalCode: null,
  country: null,
  taxOffice: null,
  taxNumber: null,
  phone: null,
  email: null,
  website: null,
  contactName: null,
  contactTitle: null,
  contactPhone: null,
  contactEmail: null,
  notes: null,
});

// --- Kontrat dönemleri ---

/** Bitişe bu kadar gün (dahil) kala kontrat "yaklaşıyor" sayılır. */
export const CONTRACT_EXPIRING_DAYS = 30;
/** Tek dönemin en uzun süresi (yıl); yazım hatasına karşı. */
export const CONTRACT_MAX_YEARS = 10;
/** Düzeltme gerekçesinin en kısa uzunluğu. */
export const CONTRACT_CORRECTION_REASON_MIN = 5;

function isRealDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const ms = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(ms) && new Date(ms).toISOString().slice(0, 10) === value;
}

const isoDate = z
  .string(typed('INVALID_DATE'))
  .transform((value) => value.trim())
  .refine(isRealDate, 'INVALID_DATE');

/**
 * Bir dönemin alanları (ADR 0140 §2): kontrat no, başlangıç ve bitiş (ikisi dahil), isteğe bağlı
 * plan ve gemi sayısı üst sınırı.
 */
export const contractPeriodSchema = z
  .strictObject({
    contractNo: optionalText(60),
    startsOn: isoDate,
    endsOn: isoDate,
    plan: optionalText(80),
    vesselLimit: z
      .number(typed('INVALID_NUMBER'))
      .int('INVALID_NUMBER')
      .min(1, 'TOO_SMALL')
      .max(10_000, 'TOO_LARGE')
      .nullish()
      .transform((value) => value ?? null),
  })
  .superRefine((value, ctx) => {
    if (value.endsOn < value.startsOn) {
      ctx.addIssue({ code: 'custom', message: 'END_BEFORE_START', path: ['endsOn'] });
      return;
    }
    const limit = `${Number(value.startsOn.slice(0, 4)) + CONTRACT_MAX_YEARS}${value.startsOn.slice(4)}`;
    if (value.endsOn > limit) {
      ctx.addIssue({ code: 'custom', message: 'CONTRACT_TOO_LONG', path: ['endsOn'] });
    }
  });
export type ContractPeriodInput = z.output<typeof contractPeriodSchema>;

export function parseContractPeriod(input: unknown): ParseResult<ContractPeriodInput> {
  const result = contractPeriodSchema.safeParse(input ?? {});
  return result.success
    ? { ok: true, value: result.data }
    : { ok: false, issues: toIssues(result.error) };
}

/** İlk kontrat, yenileme ya da gerekçeli düzeltme. */
export type ContractPeriodKind = 'initial' | 'renewal' | 'correction';

/** Kayıtlı dönem (değiştirilemez satır). */
export interface ContractPeriodRecord extends ContractPeriodInput {
  id: string;
  periodNo: number;
  kind: ContractPeriodKind;
  /** Düzeltmenin yerine geçtiği dönem. */
  replacesPeriodId: string | null;
  reason: string | null;
}

/** Türetilen durum: kontrat yok, henüz başlamadı, aktif, bitişe ≤ 30 gün, süresi doldu. */
export type TenantContractStatus = 'none' | 'scheduled' | 'active' | 'expiring' | 'expired';

export interface ContractSummary<P extends ContractPeriodRecord = ContractPeriodRecord> {
  status: TenantContractStatus;
  /** En geç biten geçerli dönem. */
  current: P | null;
  endsOn: string | null;
  /** Bitişe kalan gün (bitiş günü 0; geçtiyse eksi). */
  daysLeft: number | null;
}

/** Düzeltmeyle yerine başkası geçmemiş dönemler, dönem sırasıyla. */
export function effectivePeriods<P extends ContractPeriodRecord>(periods: readonly P[]): P[] {
  const replaced = new Set(
    periods.map((p) => p.replacesPeriodId).filter((id): id is string => id !== null),
  );
  return periods.filter((p) => !replaced.has(p.id)).sort((a, b) => a.periodNo - b.periodNo);
}

/**
 * Durum bugüne göre türetilir (saklanmaz; ADR 0140 §2). Bitiş geçerli dönemlerin en geç bitişidir;
 * erken yenilemede bugünü kapsayan eski dönem "aktif" sayar. Süresi dolan kontrat şirketi askıya
 * almaz: askıya alma elle yapılır (ADR 0137 §A).
 */
export function contractSummary<P extends ContractPeriodRecord>(
  periods: readonly P[],
  today: string,
): ContractSummary<P> {
  const effective = effectivePeriods(periods);
  if (effective.length === 0) {
    return { status: 'none', current: null, endsOn: null, daysLeft: null };
  }
  const current = effective.reduce((best, p) =>
    p.endsOn > best.endsOn || (p.endsOn === best.endsOn && p.periodNo > best.periodNo) ? p : best,
  );
  const daysLeft = daysBetween(today, current.endsOn);
  const covered = effective.some((p) => p.startsOn <= today && today <= p.endsOn);
  let status: TenantContractStatus;
  if (daysLeft < 0) status = 'expired';
  else if (!covered) status = 'scheduled';
  else if (daysLeft <= CONTRACT_EXPIRING_DAYS) status = 'expiring';
  else status = 'active';
  return { status, current, endsOn: current.endsOn, daysLeft };
}

export type ContractChange =
  | { kind: 'initial'; input: ContractPeriodInput }
  | { kind: 'renewal'; input: ContractPeriodInput }
  | {
      kind: 'correction';
      input: ContractPeriodInput;
      replacesPeriodId: string;
      reason: string;
    };

/**
 * Yeni dönem satırının kuralları (ADR 0140 §2). Geçmiş satırlar değişmez; düzeltme, geçerli bir
 * dönemin yerine gerekçeyle geçen yeni bir satırdır (PMS kaydındaki "gerekçeli iptal + yeni kayıt"
 * kalıbı [NR496 4.5.3f]).
 * - İlk kontrat: hiç geçerli dönem yokken (`CONTRACT_EXISTS`).
 * - Yenileme: kontrat olmalı (`CONTRACT_NOT_FOUND`), bitişi uzatmalı (`CONTRACT_NOT_EXTENDED`) ve
 *   bugünkü dönemden sonra başlamalı (`RENEWAL_STARTS_TOO_EARLY`); erken yenileme çakışabilir.
 * - Düzeltme: geçerli bir dönem (`PERIOD_NOT_FOUND`, `PERIOD_ALREADY_REPLACED`) ve en az 5
 *   karakter gerekçe (`REASON_REQUIRED`).
 */
export function checkContractChange(
  periods: readonly ContractPeriodRecord[],
  change: ContractChange,
): void {
  const effective = effectivePeriods(periods);
  if (change.kind === 'initial') {
    if (effective.length > 0) throw new DomainError('CONTRACT_EXISTS');
    return;
  }
  if (change.kind === 'renewal') {
    const { current } = contractSummary(periods, change.input.startsOn);
    if (!current) throw new DomainError('CONTRACT_NOT_FOUND');
    if (change.input.endsOn <= current.endsOn) throw new DomainError('CONTRACT_NOT_EXTENDED');
    if (change.input.startsOn <= current.startsOn) {
      throw new DomainError('RENEWAL_STARTS_TOO_EARLY');
    }
    return;
  }
  const target = periods.find((p) => p.id === change.replacesPeriodId);
  if (!target) throw new DomainError('PERIOD_NOT_FOUND');
  if (!effective.some((p) => p.id === target.id)) {
    throw new DomainError('PERIOD_ALREADY_REPLACED');
  }
  if (change.reason.trim().length < CONTRACT_CORRECTION_REASON_MIN) {
    throw new DomainError('REASON_REQUIRED');
  }
}
